import _ from 'lodash';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Roles } from '../../utils/enums/roles.enum';
import { UsageType } from '../../utils/enums/usage-type.enum';

export interface SaveFeedbackPayload {
  device: string;
  deviceOS: string;
  deviceOSVersion?: string | null;
  browser?: string | null;
  browserVersion?: string | null;
  releaseVersion?: string | null;
  feedbackSubmittedPage: string;
  feedback: string;
}

export interface IDesignations {
  id: number;
  designation: string;
}

export interface IUsage {
  exceeded: boolean;
  limit: number;
  type: UsageType;
  usage: number;
}
export interface IGetUserList {
  name: string;
  userId: string;
}

export interface UserUpdateFormData {
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  file: File | null;
}
export interface IUserSlice {
  componentStatus: { [name: string]: undefined | string | boolean };
  userId: string;
  email: string;
  role: Roles | string;
  username: string;
  firstName: string;
  familyName: string;
  phone: string;
  avatar: string;
  permissions: string[];
  clinicId: string | null;
  clinicName: string | null;
  designations: IDesignations[];
  organizationId: string | null;
  usage: IUsage[];
  userList?: IGetUserList[];
  isPinSkipped?: boolean;
  updateUserStatus?: boolean;
  updateUserError?: string | null;
  error?: string;
  tawkHush?: string;
  isPaymentOverdue?: string;
  companyName?: string;
  systemId?: string;
  isNccUser?: boolean;
}

export const initialState: IUserSlice = {
  userId: '',
  email: '',
  role: '',
  username: '',
  firstName: '',
  familyName: '',
  phone: '',
  avatar: '',
  permissions: [],
  clinicId: null,
  clinicName: '',
  componentStatus: {},
  designations: [],
  organizationId: '',
  usage: [],
  updateUserStatus: false,
  updateUserError: null,
  tawkHush: '',
  companyName: '',
  systemId: '',
  isNccUser: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setComponentStatus(state, action: PayloadAction<{ [key: string]: undefined | string | boolean }>) {
      state.componentStatus = { ...state.componentStatus, ...action.payload };
    },
    clearComponentStatus(state, action: PayloadAction<string[] | undefined>) {
      if (action.payload?.length) {
        state.componentStatus = _.omit(state.componentStatus, action.payload);
      } else {
        state.componentStatus = {};
      }
    },
    setUser(state, action: PayloadAction<Partial<IUserSlice>>) {
      state.userId = action.payload.userId ? action.payload.userId : state.userId;
      state.organizationId = action.payload.organizationId ? action.payload.organizationId : state.organizationId;
      state.role = action.payload.role ? action.payload.role : state.role;
      state.email = action.payload.email ? action.payload.email : state.email;
      state.username = action.payload.username ? action.payload.username : state.username;
      state.firstName = action.payload.firstName ? action.payload.firstName : state.firstName;
      state.familyName = action.payload.familyName ? action.payload.familyName : state.familyName;
      state.phone = action.payload.phone ? action.payload.phone : state.phone;
      state.avatar = action.payload.avatar ? action.payload.avatar : state.avatar;
      state.permissions = action.payload.permissions ? action.payload.permissions : state.permissions;
      state.clinicId = action.payload.clinicId ? action.payload.clinicId : state.clinicId;
      state.clinicName = action.payload.clinicName ? action.payload.clinicName : state.clinicName;
      state.usage = action.payload.usage ? action.payload.usage : state.usage;
      state.isPinSkipped = action.payload.isPinSkipped ? action.payload.isPinSkipped : state.isPinSkipped;
      state.tawkHush = action.payload.tawkHush ? action.payload.tawkHush : state.tawkHush;
      state.isPaymentOverdue = action.payload.isPaymentOverdue
        ? action.payload.isPaymentOverdue
        : state.isPaymentOverdue;
      state.companyName = action.payload.companyName ? action.payload.companyName : state.companyName;
      state.systemId = action.payload.systemId ? action.payload.systemId : state.systemId;
      state.isNccUser = action.payload.isNccUser ? action.payload.isNccUser : state.isNccUser;
    },
    getUserAsync: (_state) => {},
    getUserList: (_state: IUserSlice) => {},
    setUserList: (state, action: PayloadAction<IGetUserList[]>) => {
      state.userList = [...action.payload];
    },
    userUpdateAsync: (_state, _action: PayloadAction<{ data: Partial<IUserSlice>; image: FormData | undefined }>) => {},
    sendEmailVerificationCodeAsync: (_state, _action: PayloadAction<{ email: string }>) => {},
    changePasswordAsync: (_state, _action: PayloadAction<{ password: string; newPassword: string }>) => {},
    saveFeedback(_state, _action: PayloadAction<SaveFeedbackPayload>) {},
    getDesignationsAsync(_state, _action: PayloadAction) {},
    setDesignations(state, action: PayloadAction<IDesignations[]>) {
      state.designations = action.payload;
    },
    updateUserPinAsync: (_state, _action: PayloadAction<{ pin: string | null }>) => {},
    updateUserStart(state) {
      state.updateUserStatus = true;
      state.updateUserError = null;
    },
    updateUserSuccess(state, action: PayloadAction<Partial<IUserSlice>>) {
      state.updateUserStatus = false;
      Object.assign(state, action.payload); // Update state with API response
    },
    updateUserFailure(state, action: PayloadAction<string>) {
      state.updateUserStatus = false;
      state.updateUserError = action.payload;
    },
    resetUpdateSuccess(state) {
      state.updateUserStatus = false;
    },
    updateUserAsync: (_state, _action: PayloadAction<UserUpdateFormData>) => {},
    changePinAsync: (_state, _action: PayloadAction<{ newPin: string }>) => {},
    checkPasswordAsync: (_state, _action: PayloadAction<{ password: string }>) => {},
    checkPasswordSuccess: (state) => {
      state.componentStatus.isPasswordCorrect = true;
      state.componentStatus.isCurrentPasswordIncorrect = false;
      state.componentStatus.checkPasswordError = undefined;
    },
    checkPasswordFailure: (state, action: PayloadAction<string>) => {
      if (action.payload === 'The current password is incorrect') {
        state.componentStatus.isCurrentPasswordIncorrect = true;
      } else {
        state.componentStatus.checkPasswordError = action.payload;
      }
      state.componentStatus.isPasswordCorrect = false;
    },
  },
});

export const { actions: userActions, reducer: userReducer } = userSlice;
